import { createSelector } from 'reselect';
import { resolve } from 'src/helpers/utilities';
import { getAccessoriesTotal } from 'helpers/accessories';
import * as helpers from './helpers';

export const bikeModelSelector = createSelector(
  state => state.bike.model,
  bike => bike
);

export const bikeColoursSelector = createSelector(
  state => resolve(state, 'bike.model.colours', []),
  colours => colours
);

export const bikeColourIdSelector = createSelector(
  state => state.bike.configuration.colour,
  colourId => colourId
);

export const bikeColourSelector = createSelector(
  bikeColoursSelector,
  bikeColourIdSelector,
  (colours, selectedColourId) =>
    colours.find(colour => colour.id === selectedColourId) || colours.find(colour => colour.isDefault)
);

export const labelsSelector = createSelector(
  state => state.app.data.labels,
  labels => labels
);

export const heroSelector = createSelector(
  state => state.app.data.hero.heroTitle,
  state => state.app.data.hero.heroImage,
  (title, image) => ({ title, image })
);

export const getBikeAccessories = createSelector(
  state => state.bike.configuration.accessories,
  accessories => accessories
);

export const getPackAccessories = createSelector(
  state => state.bike.configuration.pack.accessories,
  packAccessories => packAccessories
);

export const getAllBikeAccessories = createSelector(
  getBikeAccessories,
  getPackAccessories,
  (accessories, packAccessories) => helpers.removeDuplicateAccessories([...accessories, ...packAccessories])
);

export const bikeSummaryAccessoriesSelector = createSelector(
  state => state.bike.configuration.accessories,
  accessories => accessories.filter(accessory => !accessory.pack || accessory.quantity > 1)
);

export const bikeConfigurationSelector = createSelector(
  state => state.bike.configuration,
  configuration => configuration
);

export const getPendingAccessories = createSelector(
  state => state.bike.configuration.pendingAccessories,
  pendingAccessories => pendingAccessories
);

export const getConflictingAccessories = createSelector(
  state => state.bike.configuration.pendingAccessories,
  state => state.bike.configuration.accessories,
  state => state.bike.configuration.pack.accessories,
  state => state.configurator.accessories.items,
  (pendingAccessories, bikeAccessories, packAccessories, allAccessories) =>
    helpers.getConflictingAccessories(pendingAccessories, bikeAccessories, packAccessories, allAccessories)
);

export const getPendingConflictingAccessories = createSelector(
  getConflictingAccessories,
  state => state.bike.configuration.pendingAccessories,
  (conflictingAccessories, pendingAccessories) =>
    helpers.getPendingConflictingAccessories(conflictingAccessories, pendingAccessories)
);

export const getRequiresAccessories = createSelector(
  state => state.bike.configuration.pendingAccessories,
  state => state.configurator.accessories.items,
  state => state.bike.configuration.accessories,
  getPackAccessories,
  (pendingAccessories, allAccessories, bikeAccessories, packAccessories) => {
    const emptyResponse = {
      requiresAll: [],
      requiresSingle: [],
    };

    if (!Array.isArray(pendingAccessories) || !pendingAccessories.length) {
      return emptyResponse;
    }

    const pendingAccessory = pendingAccessories[0];
    const { requiresAll, requiresSingle } = pendingAccessory;
    if (!requiresAll.length && !requiresSingle.length) {
      return emptyResponse;
    }

    const allBikeAccessories = [...bikeAccessories, ...packAccessories];
    const allBikeAccessoryIds = allBikeAccessories.map(accessory => accessory.id);

    const mapToAccessories = accessoryIdArray =>
      accessoryIdArray
        .map(accessoryId => allAccessories.find(accessory => accessory.id === accessoryId))
        .filter(accessory => accessory && !allBikeAccessories.find(bikeAccessory => bikeAccessory.id === accessory.id));

    const bikeHasRequiresSingle = requiresSingle.some(accessoryId => allBikeAccessoryIds.includes(accessoryId));

    return {
      requiresAll: mapToAccessories(requiresAll),
      requiresSingle: bikeHasRequiresSingle ? [] : mapToAccessories(requiresSingle),
    };
  }
);

export const getConflictChoice = createSelector(
  state => state.bike.configuration.conflictChoice,
  conflictChoice => conflictChoice
);

export const getRequiresSingleChoice = createSelector(
  state => state.bike.configuration.requiresSingleChoice,
  state => state.configurator.accessories.items,
  (requiresSingleChoice, accessories) => accessories.find(accessory => accessory.id === requiresSingleChoice)
);

export const getAccessoryPendingRemoval = createSelector(
  state => state.bike.configuration.accessoryPendingRemoval,
  state => state.configurator.accessories.items,
  state => state.bike.configuration.accessories,
  state => state.bike.configuration.pack.accessories,
  (accessoryPendingRemovalId, allAccessories, bikeAccessories, bikePackAccessories) => {
    const accessoryPendingRemoval = allAccessories.find(accessory => accessory.id === accessoryPendingRemovalId);

    if (!accessoryPendingRemoval) {
      return null;
    }

    return {
      ...accessoryPendingRemoval,
      linkedAccessories: [...bikeAccessories, ...bikePackAccessories].filter(
        accessory =>
          accessory.requiresSingle.includes(accessoryPendingRemovalId) ||
          accessory.requiresAll.includes(accessoryPendingRemovalId) ||
          accessoryPendingRemoval.requiresSingle.includes(accessory.id) ||
          accessoryPendingRemoval.requiresAll.includes(accessory.id)
      ),
    };
  }
);

export const getPack = createSelector(
  state => state.bike.configuration.pack.selected,
  pack => pack
);

export const getPendingPack = createSelector(
  state => state.bike.configuration.pack.pending,
  pendingPack => pendingPack
);

export const getPreviewedPackConflicts = createSelector(
  state => state.bike.configuration.pack.previewed,
  state => state.bike.accessories,
  (previewedPack, bikeAccessories) =>
    previewedPack ? helpers.getPackAccessoryConflicts(previewedPack, bikeAccessories) : []
);

export const getPendingPackConflicts = createSelector(
  state => state.bike.configuration.pack.pending,
  state => state.bike.configuration.accessories,
  (pendingPack, bikeAccessories) =>
    pendingPack && bikeAccessories.length ? helpers.getPackAccessoryConflicts(pendingPack, bikeAccessories) : []
);

export const getPackPendingRemoval = createSelector(
  state => state.bike.configuration.pack.pendingRemoval,
  packPendingRemoval => packPendingRemoval
);

export const getPackAndAccessoriesTotal = createSelector(
  state => state.bike.configuration.accessories,
  state => state.bike.configuration.pack.selected,
  state => state.bike.configuration.pack.accessories,
  (accessories, pack, packAccessories) => {
    const accessoriesTotal = accessories.reduce(getAccessoriesTotal, 0);
    const packTotal = pack && pack.isLocked ? pack.price : packAccessories.reduce(getAccessoriesTotal, 0);

    return accessoriesTotal + packTotal;
  }
);

export const getPreviewedPack = createSelector(
  state => state.bike.configuration.pack.previewed,
  previewedPack => previewedPack
);

export const getPackSelectedAccessories = createSelector(
  state => state.bike.configuration.pack.selectedAccessories,
  selectedAccessories => selectedAccessories
);

export const getPackTotal = createSelector(
  getPack,
  getPreviewedPack,
  getPackAccessories,
  (pack, previewedPack, packAccessories) => {
    const currentPack = pack || previewedPack;

    if (!currentPack) {
      return 0;
    }

    return currentPack && currentPack.isLocked
      ? currentPack.price
      : packAccessories.reduce((prevTotal, accessory) => prevTotal + accessory.price, 0);
  }
);

export const getPackFittingTimeTotal = createSelector(
  getPack,
  getPreviewedPack,
  getPackSelectedAccessories,
  (pack, previewedPack, packSelectedAccessories) => {
    const currentPack = pack || previewedPack;

    if (!currentPack) {
      return 0;
    }

    return currentPack && currentPack.isLocked
      ? currentPack.fittingTime
      : packSelectedAccessories.reduce((prevTotal, accessory) => prevTotal + accessory.fittingTime, 0);
  }
);

export const getPackSelectedTotal = createSelector(
  getPack,
  getPreviewedPack,
  getPackSelectedAccessories,
  (pack, previewedPack, packSelectedAccessories) => {
    const currentPack = pack || previewedPack;

    if (!currentPack) {
      return 0;
    }

    return currentPack && currentPack.isLocked
      ? currentPack.price
      : packSelectedAccessories.reduce((prevTotal, accessory) => prevTotal + accessory.price, 0);
  }
);

export const getPreviewedPackTotal = createSelector(
  getPreviewedPack,
  getPackSelectedAccessories,
  getPendingConflictingAccessories,
  (previewedPack, packSelectedAccessories, pendingAccessories) => {
    const pendingAccessoriesIds = pendingAccessories.map(accessory => accessory.id);

    return previewedPack && previewedPack.isLocked
      ? previewedPack.price
      : packSelectedAccessories
          .filter(accessory => !pendingAccessoriesIds.includes(accessory.id))
          .reduce((prevTotal, accessory) => prevTotal + accessory.price, 0);
  }
);

export const getSelectedWarrantyPack = createSelector(
  state => state.bike.configuration.warrantyPack,
  warrantyPack => warrantyPack
);

export const getSelectedServicePack = createSelector(
  state => state.bike.configuration.servicePack,
  servicePack => servicePack
);
